import {
  getDocs as _getDocs,
  getDocsFromCache as _getDocsFromCache,
  getDoc as _getDoc,
  getCountFromServer as _getCountFromServer,
  terminate as _terminate,
  clearIndexedDbPersistence as _clearIndexedDbPersistence,
} from 'firebase/firestore'
import type { DocumentReference, Query, DocumentData, Firestore } from 'firebase/firestore'
import { log, logFile } from '../logger'
import { bold, dim, hdr, logGetDocRes, logGetDocsRes, refPath, rethrowTagged } from './log'
import { meterReads } from './readMeter'
import { getTimer } from './latency'

// A network getDocs/getDoc has no timeout: on a slow-but-alive connection the
// SDK never flips to "offline", so the call can hang for 30s+ (airplane mode
// resolves instantly from cache). Warn when one is still pending so blocked
// requests name themselves in the boot log instead of being invisible.
const PENDING_WARN_MS = 5_000

function warnIfPending(op: string, path: string): () => void {
  const timer = setTimeout(() => {
    log('fs', `${op} still pending after ${PENDING_WARN_MS}ms — slow network?`, { path })
  }, PENDING_WARN_MS)
  return () => clearTimeout(timer)
}

export function getDocs<T>(ref: Query<T>) {
  const p = refPath(ref)
  const settled = warnIfPending('getDocs', p)
  const timed = getTimer(p)
  return _getDocs(ref)
    .finally(settled)
    .then(snap => {
      timed(snap.metadata.fromCache, snap.size)
      logGetDocsRes({ path: p, n: snap.size, fromCache: snap.metadata.fromCache })
      logFile('fs', 'getDocs', { path: p, n: snap.size, fromCache: snap.metadata.fromCache })
      if (!snap.metadata.fromCache) meterReads(p, Math.max(1, snap.size))
      return snap
    }, rethrowTagged('getDocs', p))
}

// Cache-only read: resolves immediately with whatever the local cache has for
// this query — including an empty result. Never blocks on the network; an
// empty snapshot is a valid answer ("cached and genuinely empty", e.g. a party
// with no lineup). Callers that suspect the empty result means "never synced"
// fire their own background getDocs and merge the late result. Only a cache
// read *error* (persistence unavailable) falls back to the network.
export function getDocsCacheFirst<T>(ref: Query<T>) {
  const p = refPath(ref)
  const timed = getTimer(p)
  return _getDocsFromCache(ref).then(
    snap => {
      timed(true, snap.size)
      logGetDocsRes({ path: p, n: snap.size, fromCache: true })
      logFile('fs', 'getDocs', { path: p, n: snap.size, fromCache: true })
      return snap
    },
    () => {
      const settled = warnIfPending('getDocs', p)
      return _getDocs(ref)
        .finally(settled)
        .then(netSnap => {
          timed(netSnap.metadata.fromCache, netSnap.size)
          logGetDocsRes({ path: p, n: netSnap.size, fromCache: netSnap.metadata.fromCache })
          logFile('fs', 'getDocs', { path: p, n: netSnap.size, fromCache: netSnap.metadata.fromCache })
          if (!netSnap.metadata.fromCache) meterReads(p, Math.max(1, netSnap.size))
          return netSnap
        }, rethrowTagged('getDocs', p))
    },
  )
}

export function getDoc<T extends DocumentData>(ref: DocumentReference<T>) {
  const settled = warnIfPending('getDoc', ref.path)
  const timed = getTimer(ref.path)
  return _getDoc(ref)
    .finally(settled)
    .then(snap => {
      timed(snap.metadata.fromCache, 1)
      logGetDocRes({ path: ref.path, exists: snap.exists(), fromCache: snap.metadata.fromCache })
      logFile('fs', 'getDoc', { path: ref.path, exists: snap.exists(), fromCache: snap.metadata.fromCache })
      if (!snap.metadata.fromCache) meterReads(ref.path, 1)
      return snap
    }, rethrowTagged('getDoc', ref.path))
}

export function getCountFromServer<T>(ref: Query<T>) {
  const p = refPath(ref)
  return _getCountFromServer(ref).then(snap => {
    const n = snap.data().count
    console.log(`%c${hdr('count', `${p}  (${n})`)}`, dim)
    logFile('fs', 'count', { path: p, n })
    // Billed one read per 1,000 index entries matched, minimum one.
    meterReads(p, Math.max(1, Math.ceil(n / 1000)))
    return snap
  }, rethrowTagged('count', p))
}

export function terminate(db: Firestore) {
  console.log(`%c${hdr('terminate', '')}`, bold)
  logFile('fs', 'terminate')
  return _terminate(db)
}

export function clearIndexedDbPersistence(db: Firestore) {
  console.log(`%c${hdr('clearPersistence', '')}`, bold)
  logFile('fs', 'clearPersistence')
  return _clearIndexedDbPersistence(db)
}
